/**
 * Price indexes from a basket of goods.
 *
 * The base period is the one the index compares against (index = 100). A Laspeyres index
 * prices the base-period basket in both periods — the design of the U.S. consumer price
 * index — a Paasche index prices the current-period basket, and the Fisher ideal index is
 * the geometric mean of the two.
 */

export interface BasketItem {
  name: string;
  /** Price in the base period. */
  basePrice: number;
  /** Quantity in the base period. */
  baseQuantity: number;
  /** Price in the current period. */
  currentPrice: number;
  /** Quantity in the current period. */
  currentQuantity: number;
}

export interface ItemDetail {
  name: string;
  /** Current price ÷ base price. */
  priceRelative: number;
  /** Base-period spending on the item ÷ base-period spending on the basket. */
  baseShare: number;
  /** Percentage points of the Laspeyres change this item contributes, as a decimal. */
  contribution: number;
}

export interface BasketResult {
  /** p0 × q0 summed: the base basket at base prices. */
  baseBasketBase: number;
  /** p1 × q0 summed: the base basket at current prices. */
  baseBasketCurrent: number;
  /** p0 × q1 summed: the current basket at base prices. */
  currentBasketBase: number;
  /** p1 × q1 summed: the current basket at current prices. */
  currentBasketCurrent: number;
  laspeyres: number;
  paasche: number;
  fisher: number;
  items: ItemDetail[];
}

/** The position of the first good with an unusable price or quantity, or -1. */
export function badItemIndex(items: readonly BasketItem[]): number {
  return items.findIndex(
    (item) =>
      !(
        item.basePrice > 0 &&
        item.currentPrice > 0 &&
        item.baseQuantity >= 0 &&
        item.currentQuantity >= 0
      )
  );
}

/** True when the basket can be priced: positive prices, non-negative quantities. */
export function validBasket(items: readonly BasketItem[]): boolean {
  if (items.length === 0) {
    return false;
  }
  const finite = items.every((item) =>
    [
      item.basePrice,
      item.baseQuantity,
      item.currentPrice,
      item.currentQuantity,
    ].every(Number.isFinite)
  );
  if (!finite) {
    return false;
  }
  const prices = items.every(
    (item) => item.basePrice > 0 && item.currentPrice > 0
  );
  const quantities = items.every(
    (item) => item.baseQuantity >= 0 && item.currentQuantity >= 0
  );
  const baseSpending = items.reduce(
    (sum, item) => sum + item.basePrice * item.baseQuantity,
    0
  );
  const currentBasketBase = items.reduce(
    (sum, item) => sum + item.basePrice * item.currentQuantity,
    0
  );
  return prices && quantities && baseSpending > 0 && currentBasketBase > 0;
}

/** Laspeyres, Paasche and Fisher indexes (base = 100) for a valid basket. */
export function basketIndexes(items: readonly BasketItem[]): BasketResult {
  const sum = (value: (item: BasketItem) => number) =>
    items.reduce((total, item) => total + value(item), 0);
  const baseBasketBase = sum((i) => i.basePrice * i.baseQuantity);
  const baseBasketCurrent = sum((i) => i.currentPrice * i.baseQuantity);
  const currentBasketBase = sum((i) => i.basePrice * i.currentQuantity);
  const currentBasketCurrent = sum((i) => i.currentPrice * i.currentQuantity);
  const laspeyres = (baseBasketCurrent / baseBasketBase) * 100;
  const paasche = (currentBasketCurrent / currentBasketBase) * 100;
  return {
    baseBasketBase,
    baseBasketCurrent,
    currentBasketBase,
    currentBasketCurrent,
    laspeyres,
    paasche,
    fisher: Math.sqrt(laspeyres * paasche),
    items: items.map((item) => ({
      name: item.name,
      priceRelative: item.currentPrice / item.basePrice,
      baseShare: (item.basePrice * item.baseQuantity) / baseBasketBase,
      contribution:
        ((item.currentPrice - item.basePrice) * item.baseQuantity) /
        baseBasketBase,
    })),
  };
}

/** The inflation rate between two index values, as a decimal. */
export function inflationBetween(earlier: number, later: number): number {
  return later / earlier - 1;
}

/** The average yearly rate over `years` between two index values, as a decimal. */
export function annualisedInflation(
  earlier: number,
  later: number,
  years: number
): number {
  return (later / earlier) ** (1 / years) - 1;
}

/** An index value restated so that `newBase` equals 100. */
export function rebase(value: number, newBase: number): number {
  return (value / newBase) * 100;
}
